import React, { useState, useContext } from 'react';
import { Form, Input, Button, Typography, Card, Steps, Modal } from 'antd';
import {
  MailOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { LanguageContext } from './LanguageContext';

const { Title, Text } = Typography;
const { Step } = Steps;

const BASE_URL = 'https://json-configrator-api.root.myoctoopidev.com';

const ForgotPassword = () => {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ visible: false, message: '' });
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [form3] = Form.useForm();
  const navigate = useNavigate();
  const { language, setLanguage, translations } = useContext(LanguageContext);
  const t = translations[language];

  const showError = (msg) => setErrorModal({ visible: true, message: msg });

  const handleSendCode = async ({ email: mail }) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Auth/ForgotPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: mail }),
      });
      const data = await res.json();
      if (data.success) {
        setEmail(mail);
        setStep(1);
      } else {
        showError(data.message || t.birhataolustu);
      }
    } catch {
      showError(t.fp_serverError);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async ({ code }) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Auth/VerifyResetCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (data.success && data.data?.resetToken) {
        setResetToken(data.data.resetToken);
        setStep(2);
      } else {
        showError(data.message || t.birhataolustu);
      }
    } catch {
      showError(t.fp_serverError);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async ({ newPassword }) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Auth/ResetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, resetToken, newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setStep(3);
      } else {
        showError(data.message || t.birhataolustu);
      }
    } catch {
      showError(t.fp_serverError);
    } finally {
      setLoading(false);
    }
  };

  const btnStyle = {
    height: 44,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    border: 'none',
    fontWeight: 600,
    fontSize: 15,
    boxShadow: '0 4px 14px rgba(24,144,255,0.4)',
  };

  const stepIcons = [
    { icon: <MailOutlined style={{ fontSize: 28, color: '#fff' }} />, color: ['#1890ff', '#096dd9'] },
    { icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#fff' }} />, color: ['#1890ff', '#096dd9'] },
    { icon: <LockOutlined style={{ fontSize: 28, color: '#fff' }} />, color: ['#1890ff', '#096dd9'] },
    { icon: <CheckCircleOutlined style={{ fontSize: 28, color: '#fff' }} />, color: ['#52c41a', '#389e0d'] },
  ];

  const current = stepIcons[step];

  return (
    <div style={{
      backgroundColor: '#001529',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 32px', borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <span style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>{t.layoutTitle}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 6,
              color: '#fff',
              padding: '6px 10px',
              fontSize: 14,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="tr" style={{ color: '#000', background: '#fff' }}>Türkçe</option>
            <option value="en" style={{ color: '#000', background: '#fff' }}>English</option>
            <option value="de" style={{ color: '#000', background: '#fff' }}>Deutsch</option>
          </select>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate('/login')}
            style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}
          >
            {t.fp_backToLogin}
          </Button>
        </div>
      </div>

      {/* İçerik */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px 16px' }}>
        <Card
          style={{ width: 420, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: 'none' }}
          bodyStyle={{ padding: '36px 36px 28px' }}
        >
          {step < 3 && (
            <Steps current={step} size="small" style={{ marginBottom: 28 }}>
              <Step title={t.fp_stepEmail} />
              <Step title={t.fp_stepVerify} />
              <Step title={t.fp_stepNewPass} />
            </Steps>
          )}

          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: `linear-gradient(135deg, ${current.color[0]} 0%, ${current.color[1]} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
              boxShadow: '0 8px 24px rgba(24,144,255,0.3)',
            }}>
              {current.icon}
            </div>

            {step === 0 && <>
              <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#262626' }}>{t.fp_title}</Title>
              <Text style={{ color: '#8c8c8c', fontSize: 13 }}>{t.fp_subtitle}</Text>
            </>}
            {step === 1 && <>
              <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#262626' }}>{t.fp_codeTitle}</Title>
              <Text style={{ color: '#8c8c8c', fontSize: 13 }}><b>{email}</b> {t.fp_codeSubtitle}</Text>
            </>}
            {step === 2 && <>
              <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#262626' }}>{t.fp_newPassTitle}</Title>
              <Text style={{ color: '#8c8c8c', fontSize: 13 }}>{t.fp_newPassSubtitle}</Text>
            </>}
            {step === 3 && <>
              <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#262626' }}>{t.fp_successTitle}</Title>
              <Text style={{ color: '#8c8c8c', fontSize: 13 }}>{t.fp_successSubtitle}</Text>
            </>}
          </div>

          {/* ADIM 0 — Email */}
          {step === 0 && (
            <Form form={form1} layout="vertical" onFinish={handleSendCode}>
              <Form.Item
                name="email"
                label={<span style={{ fontWeight: 500 }}>{t.fp_emailLabel}</span>}
                rules={[
                  { required: true, message: t.fp_emailRequired },
                  { type: 'email', message: t.fp_emailInvalid },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="ornek@domain.com"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" block loading={loading} style={btnStyle}>
                  {t.fp_sendCode}
                </Button>
              </Form.Item>
            </Form>
          )}

          {/* ADIM 1 — Kod */}
          {step === 1 && (
            <Form form={form2} layout="vertical" onFinish={handleVerifyCode}>
              <Form.Item
                name="code"
                label={<span style={{ fontWeight: 500 }}>{t.fp_codeLabel}</span>}
                rules={[
                  { required: true, message: t.fp_codeRequired },
                  { len: 6, message: t.fp_codeLength },
                ]}
              >
                <Input
                  prefix={<SafetyCertificateOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="123456"
                  maxLength={6}
                  size="large"
                  style={{ borderRadius: 8, letterSpacing: 6, textAlign: 'center', fontSize: 20, fontWeight: 600 }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" block loading={loading} style={btnStyle}>
                  {t.fp_verify}
                </Button>
              </Form.Item>
              <div style={{ textAlign: 'center' }}>
                <Button type="text" size="small" style={{ color: '#8c8c8c' }}
                  onClick={() => { setStep(0); form2.resetFields(); }}>
                  {t.fp_changeEmail}
                </Button>
              </div>
            </Form>
          )}

          {/* ADIM 2 — Yeni Şifre */}
          {step === 2 && (
            <Form form={form3} layout="vertical" onFinish={handleResetPassword}>
              <Form.Item
                name="newPassword"
                label={<span style={{ fontWeight: 500 }}>{t.fp_newPassLabel}</span>}
                rules={[
                  { required: true, message: t.fp_newPassRequired },
                  { min: 6, message: t.fp_newPassMin },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder={t.fp_newPassLabel}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label={<span style={{ fontWeight: 500 }}>{t.fp_confirmLabel}</span>}
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: t.fp_confirmRequired },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error(t.fp_confirmMismatch));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder={t.fp_confirmLabel}
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" block loading={loading} style={btnStyle}>
                  {t.fp_reset}
                </Button>
              </Form.Item>
            </Form>
          )}

          {/* ADIM 3 — Başarı */}
          {step === 3 && (
            <Button type="primary" block onClick={() => navigate('/login')} style={btnStyle}>
              {t.fp_goLogin}
            </Button>
          )}
        </Card>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '14px', color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>
        © 2026{' '}
        <a href="http://octoopi.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.55)' }}>Octoopi</a>
        {' '}&amp; by{' '}
        <a href="https://mertech.com.tr/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.55)' }}>Mertech Electronics</a>
      </div>

      {/* Hata Modal */}
      <Modal
        title={null}
        open={errorModal.visible}
        onOk={() => setErrorModal({ visible: false, message: '' })}
        onCancel={() => setErrorModal({ visible: false, message: '' })}
        okText={t.fp_goLogin}
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        width={380}
        okButtonProps={{
          style: {
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            border: 'none', height: 38, borderRadius: 6, fontWeight: 500,
          },
        }}
        bodyStyle={{ padding: '28px', textAlign: 'center' }}
      >
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
          boxShadow: '0 6px 20px rgba(255,107,107,0.3)',
        }}>
          <ExclamationCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#262626', margin: '0 0 8px' }}>{t.fp_error}</h3>
        <p style={{ fontSize: 14, color: '#595959', margin: 0 }}>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default ForgotPassword;
